import { Theme, css } from '@emotion/react';

import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';

// import { useRouter } from 'next/router';
import { Fragment, Suspense, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useStoryQuery } from '@hooks/query/useStoryQuery';
import { useReadStoryStore } from '@stores/useReadStoryStore';
import useVpDiffStore from '@stores/useVpDiffStore';
import { FontSizes, Spacing } from 'chatie-design-system';

import ChatRoomList from '@components/chat/ChatRoomList';
import BottomButton from '@components/common/BottomButton';
import HorizontalFilter from '@components/common/HorizontalFilter';
import Loading from '@components/common/Loading';
import Seo from '@components/common/Seo';
import Tab, { useTab } from '@components/common/Tab';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import { PushSize } from '@components/layout/Push';
import SafeArea from '@components/layout/SafeArea';
import CharacterSkeleton from '@components/skeleton/CharacterSkeleton';
import SkeletonList from '@components/skeleton/SkeletonList';
import StorySkeleton from '@components/skeleton/StorySkeleton';
import LikedStoryList from '@components/stories/LikedStoryList';
import ReadStoryList from '@components/stories/ReadStoryList';
import FollowingFeed from '@components/user/FollowingFeed';

import { ReadStorySort } from '@api/story';
import { QUERY_KEYS } from '@constants';
import { withAuth } from '@contexts/auth';
import { CustomPageProps } from '@model';
import { media, sticky } from '@styles/css';
import { useScreen } from '@utils/hook';
import { setFlow } from '@utils/misc';

export const LIBRARY_SIZE = 'library';

type LibraryTab =
  | 'read-stories'
  | 'liked-stories'
  | 'following-feed'
  | 'chat-library';

/**
 * Pages
 */

const libraryCSS = (diff: number) => css`
  .bottomButton {
    bottom: ${diff}px;
  }
`;

const readStoryFilterCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: row;
  text-align: left;
  padding: ${Spacing.small}px ${Spacing.medium}px;
  border-bottom: 1px solid ${theme.border.light};
  ${sticky(PushSize.tab)};
  z-index: 3;
  background-color: ${theme.var.white};
  height: 43px;

  &::after {
    content: '';
    display: table;
    clear: both;
  }

  ${media.tablet} {
    padding: ${Spacing.small}px ${Spacing.xlarge}px;
  }

  ${media.desktop} {
    padding: ${Spacing.small}px ${Spacing.xxlarge}px;
  }
`;

const editButtonGroupCSS = (theme: Theme) => css`
  margin-left: auto;

  span {
    display: inline-block;
    height: 12px;
    border-left: 1px solid ${theme.border.light};
    margin: 0 ${Spacing.xsmall}px;
    vertical-align: middle;
  }
`;

const editButtonCSS = (theme: Theme) => css`
  font-size: ${FontSizes.xsmall}px;
  color: ${theme.var.black};
`;

const LibraryDetail = ({ isCheming }: { isCheming: boolean }) => {
  const { t } = useTranslation();
  const { diff, setDiff } = useVpDiffStore();
  const initialHeight = window.innerHeight;
  useScreen('보관함');

  let pendingUpdate = false;

  const tab = useTab<LibraryTab>(
    isCheming
      ? [{ label: t('내 대화'), value: 'chat-library' }]
      : [
          { label: t('읽은 작품'), value: 'read-stories' },
          { label: t('플레이'), value: 'chat-library' },
          { label: t('팔로잉'), value: 'following-feed' },
          { label: t('좋아요'), value: 'liked-stories' },
        ],
    { replace: true }
  );

  const { activeTab } = tab;
  const { deselectAll, selectAll, selectedIds } = useReadStoryStore();
  const { removeReadStories } = useStoryQuery();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<ReadStorySort>('latest');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  useEffect(() => {
    deselectAll();
  }, [activeTab]);

  const handleToggleEditButton = () => {
    setIsEdit(!isEdit);

    if (isEdit) {
      deselectAll();
    }
  };

  const handleToggleSelectAllButton = () => {
    setIsSelectAll(!isSelectAll);

    if (isSelectAll) {
      deselectAll();
    } else {
      selectAll();
    }
  };

  const handleClickDeleteButton = async () => {
    if (confirm('읽은 작품을 삭제하시겠습니까?')) {
      removeReadStories(selectedIds, {
        onSuccess() {
          setIsEdit(false);
          deselectAll();
          queryClient.invalidateQueries(QUERY_KEYS.READ_STORY.LIST('latest'));
          queryClient.invalidateQueries(QUERY_KEYS.READ_STORY.LIST('update'));
        },
        onError() {
          alert('다시 시도해주시길 바랍니다.');
        },
      });
    }
  };

  const viewportHandler = () => {
    if (pendingUpdate) return;
    pendingUpdate = true;

    requestAnimationFrame(() => {
      const diff = window.innerHeight - initialHeight;
      pendingUpdate = false;
      const height = window.innerHeight;
      if (height > initialHeight) {
        setDiff(diff);
      }
    });
  };

  useEffect(() => {
    viewportHandler();

    window.visualViewport?.addEventListener('resize', viewportHandler);

    return () => {
      window.visualViewport?.removeEventListener('resize', viewportHandler);
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'chat-library') {
      setFlow({ isDelete: true });
      setFlow({ name: '보관함/플레이' });
    }
  }, [activeTab]);

  return (
    <div css={libraryCSS(diff)}>
      {!isCheming && <Tab {...tab} stickyAt={0} fit />}

      {activeTab === 'read-stories' && (
        <div css={readStoryFilterCSS}>
          {!isEdit && (
            <HorizontalFilter
              options={[
                { label: t('최근본 순'), value: 'latest' },
                { label: t('업데이트 순'), value: 'update' },
              ]}
              selected={sort}
              onChange={setSort}
            />
          )}
          <div css={editButtonGroupCSS}>
            {isEdit && (
              <>
                <Fragment>
                  <button
                    css={editButtonCSS}
                    onClick={handleToggleSelectAllButton}
                  >
                    {t(isSelectAll ? '전체선택 해제' : '전체선택')}
                  </button>
                </Fragment>
                <span />
              </>
            )}
            <Fragment>
              <button css={editButtonCSS} onClick={handleToggleEditButton}>
                {t(isEdit ? '취소' : '편집')}
              </button>
            </Fragment>
          </div>
        </div>
      )}

      <SafeArea bottom>
        <Suspense fallback={<SkeletonList skeleton={StorySkeleton} />}>
          {activeTab === 'read-stories' && (
            <ReadStoryList sort={sort} isEdit={isEdit} />
          )}
          {activeTab === 'following-feed' && <FollowingFeed />}
        </Suspense>
        <Suspense fallback={<SkeletonList skeleton={StorySkeleton} />}>
          {activeTab === 'liked-stories' && <LikedStoryList />}
        </Suspense>

        <Suspense fallback={<SkeletonList skeleton={CharacterSkeleton} />}>
          {activeTab === 'chat-library' && (
            <ChatRoomList isCheming={isCheming} />
          )}
        </Suspense>

        {isEdit && (
          <BottomButton
            push
            className="bottomButton"
            text={t('선택 항목 삭제')}
            onClick={handleClickDeleteButton}
          />
        )}
      </SafeArea>
    </div>
  );
};

const LibraryPage: NextPage<CustomPageProps> = ({ isCheming }) => {
  const { t } = useTranslation();

  return (
    <Layout isPush>
      <Seo title={t('보관함')} />
      {isCheming && <Header title={t('보관함')} />}
      <Suspense fallback={<Loading center />}>
        <LibraryDetail isCheming={isCheming} />
      </Suspense>
    </Layout>
  );
};

// const LibraryLoading = () => {
//   const { t } = useTranslation();

//   const tab = useTab<LibraryTab>(
//     [
//       { label: t('읽은 작품'), value: 'read-stories' },
//       { label: t('팔로잉'), value: 'following-feed' },
//       { label: t('좋아요'), value: 'liked-stories' },
//     ],
//     { replace: true }
//   );

//   return (
//     <Layout>
//       <Seo title={t('보관함')} />

//       <Tab {...tab} stickyAt={0} fit />

//       <SkeletonList skeleton={StorySkeleton} />
//     </Layout>
//   );
// };

export default withAuth(LibraryPage);
// export default withAuth(LibraryPage, { LoadingComponent: LibraryLoading });
